import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { CriticalVariableFields } from '@queries/generated/fragments.generated';
import {
    deleteCriticalVariable,
    deleteCriticalVariableVariables,
    updateCriticalVariable,
    updateCriticalVariableVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    CTQ_WIDGET_VALUES,
    DELETE_CRITICAL_VARIABLE,
    UPDATE_CRITICAL_TO_QUALITY_FACTOR,
} from '@queries/risk-assessment';

interface DeleteCriticalToQualityFactorProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
    ctqFactor: CriticalVariableFields;
}

const DeleteCriticalToQualityFactorDialog = ({ onClose, onSubmit, ctqFactor }: DeleteCriticalToQualityFactorProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.dialogs.deleteCtQf' });
    const { t: tcommon } = useTranslation('common');
    const actionTypes = ['delete', 'inactivate', 'deleteInactive'];
    const { riskCount, active } = ctqFactor;
    const associatedFunctionalDataReviewsCount = ctqFactor.associatedFunctionalDataReviewIds?.length ?? 0;
    const commentCount = ctqFactor.commentCounts.all;
    const showCounts = associatedFunctionalDataReviewsCount > 0 || commentCount > 0;
    const { current } = useContext(CTQFactorsContext);
    const { study } = current;

    type DeleteCtQFormInputs = {
        reason: string;
    };

    const {
        control,
        getValues,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<DeleteCtQFormInputs>({
        mode: 'all',
        defaultValues: { reason: '' },
    });

    const [updateCriticalToQualityFactor] = useMutation<updateCriticalVariable, updateCriticalVariableVariables>(
        UPDATE_CRITICAL_TO_QUALITY_FACTOR,
    );

    const [deleteCriticalToQualityFactor] = useMutation<deleteCriticalVariable, deleteCriticalVariableVariables>(
        DELETE_CRITICAL_VARIABLE,
    );

    let selectedAction: number;
    if ((riskCount == null || riskCount === 0) && active === true) {
        selectedAction = 0; // delete
    } else if (riskCount && riskCount > 0 && active === true) {
        selectedAction = 1; // inactivate
    } else if (riskCount && riskCount > 0 && active === false) {
        selectedAction = 2; // deleteInactive
    } else {
        selectedAction = 0;
    }

    const associatedDataWarningMessage = (
        <>
            <div className={cn('k-text-left k-mb-1')}>{t('delete.associatedDataWarningMessage')}</div>
            {associatedFunctionalDataReviewsCount > 0 && (
                <div className={cn('k-text-left')}>
                    {t('delete.functionalDataReviewsCount', {
                        count: associatedFunctionalDataReviewsCount,
                    })}
                </div>
            )}
            {commentCount > 0 && (
                <div className={cn('k-text-left')}>
                    {t('delete.commentsCount', {
                        count: commentCount,
                    })}
                </div>
            )}
            <div className={cn('k-text-left  k-mt-1')}>{t('delete.footer')}</div>
        </>
    );

    const handleSubmit = () => {
        if (selectedAction === 0) {
            deleteCriticalToQualityFactor({
                variables: { id: ctqFactor.id, reason: getValues('reason') },
                refetchQueries: [
                    {
                        query: CTQ_WIDGET_VALUES,
                        variables: { studyId: study?.id },
                    },
                ],
            })
                .then((response) => {
                    if (!response?.data?.deleteCriticalVariable) {
                        onSubmit('error');
                    }
                    onSubmit('success');
                })
                .catch(() => {
                    onSubmit('error');
                });
        } else {
            updateCriticalToQualityFactor({
                variables: {
                    criticalVariable: {
                        studyId: study?.id,
                        active: false,
                        description: ctqFactor.description ?? '',
                        justification: ctqFactor.justification,
                        typeId: ctqFactor.cTQType?.id,
                        edcentryId: ctqFactor.cTQEDCEntry?.id,
                        vendorIds: ctqFactor.vendors?.map((v) => v?.id) ?? [],
                        sourceSystem: ctqFactor.sourceSystem,
                        dataSource: ctqFactor.dataSource,
                        isCritical: ctqFactor.isCritical,
                        id: ctqFactor?.id,
                    },
                },
                refetchQueries: [
                    {
                        query: CTQ_WIDGET_VALUES,
                        variables: { studyId: study?.id },
                    },
                ],
            })
                .then(async (response) => {
                    if (!response?.data?.updateCriticalVariable) {
                        onSubmit('error');
                    }
                    onSubmit('inactivated');
                })
                .catch(() => {
                    onSubmit('error');
                });
        }
    };

    return (
        <>
            {selectedAction === 0 && (
                <DraggableDialog
                    dataTestId="delete-ctq-dialog"
                    onSubmit={() => handleSubmit()}
                    onClose={onClose}
                    title={`${t(`${actionTypes[selectedAction]}.title`)}: ${ctqFactor.recordId}`}
                    initialWidth={530}
                    initialHeight={280}
                    height={null}
                    primaryBtnLabel={tcommon('delete')}
                    primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
                >
                    <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                        {showCounts && <InfoField type="Warning" message={associatedDataWarningMessage} />}
                        <TextAreaController
                            className={style.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="reason"
                            required
                            data-testid="field-reason"
                            label={`${t('delete.deleteReasonLabel')} *`}
                        />
                    </div>
                </DraggableDialog>
            )}
            {selectedAction === 1 && (
                <ConfirmationDialog
                    title={t(`${actionTypes[selectedAction]}.title`)}
                    dataTestId="confirmation-dialog"
                    onClose={() => onClose()}
                    onSubmit={() => handleSubmit()}
                    className="info"
                >
                    <div>
                        <div style={{ width: '423px', margin: '0 0', marginBottom: '30px' }}>
                            <InfoField type="Info" message={t('inactivate.warning')} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                            {t(`${actionTypes[selectedAction]}.message`)}
                        </div>
                    </div>
                </ConfirmationDialog>
            )}
            {selectedAction === 2 && (
                <ConfirmationDialog
                    title={t(`${actionTypes[selectedAction]}.title`)}
                    dataTestId="confirmation-dialog"
                    onClose={() => onClose()}
                    secondaryBtnLabel={tcommon('close')}
                >
                    <div>
                        <div>{t(`${actionTypes[selectedAction]}.message`)}</div>
                    </div>
                </ConfirmationDialog>
            )}
        </>
    );
};

export default DeleteCriticalToQualityFactorDialog;
